export default class LocalStream {
  constructor() {
    // Queue to serialize IndexedDB writes and prevent concurrent write corruption
    this.writeQueue = Promise.resolve();
    // State flags to prevent race conditions in recording start/stop
    this.isRecordingStarting = false;
    this.isRecordingStopping = false;
    this.earlyRecorderFlushTimer = null;
    this.captureInvariantCheckInProgress = false;
    this.lastCaptureInvariantRepairAt = 0;
  }

  expectedCaptureState() {
    const videoButton = $("#video-btn i");
    const micButton = $("#mic-btn i");

    const expectedVideo =
      videoButton.length > 0
        ? videoButton.hasClass("fa-video")
        : !!(
            localTracks.videoTrack && localTracks.videoTrack._enabled !== false
          );
    const expectedAudio =
      micButton.length > 0
        ? micButton.hasClass("fa-microphone")
        : !!(
            localTracks.audioTrack && localTracks.audioTrack._enabled !== false
          );

    return { expectedVideo, expectedAudio };
  }

  localMediaStreamSatisfies(expectedState = this.expectedCaptureState()) {
    if (!local_media_stream) return false;

    const hasExpectedVideo =
      !expectedState.expectedVideo ||
      local_media_stream
        .getVideoTracks()
        .some(
          (track) => track.readyState === "live" && track.enabled !== false,
        );
    const hasExpectedAudio =
      !expectedState.expectedAudio ||
      local_media_stream
        .getAudioTracks()
        .some(
          (track) => track.readyState === "live" && track.enabled !== false,
        );

    return hasExpectedVideo && hasExpectedAudio;
  }

  async ensureCaptureInvariant({
    source = "unknown",
    forceUpdate = false,
  } = {}) {
    if (!$("#video-btn").length && !$("#mic-btn").length) {
      return true;
    }
    if (this.captureInvariantCheckInProgress) {
      return this.localMediaStreamSatisfies();
    }
    if (LocalStream._audio_track_updating || this.isRecordingStopping) {
      return this.localMediaStreamSatisfies();
    }

    const expectedState = this.expectedCaptureState();
    if (!expectedState.expectedVideo && !expectedState.expectedAudio) {
      return true;
    }
    if (this.localMediaStreamSatisfies(expectedState)) {
      return true;
    }

    this.captureInvariantCheckInProgress = true;
    try {
      const now = Date.now();
      if (!forceUpdate && now - this.lastCaptureInvariantRepairAt < 15000) {
        return this.localMediaStreamSatisfies(expectedState);
      }

      this.lastCaptureInvariantRepairAt = now;
      await this.update(false);

      if (!this.localMediaStreamSatisfies(expectedState)) {
        Rollbar.error("capture_invariant_not_repaired", {
          source,
          expectedVideo: expectedState.expectedVideo,
          expectedAudio: expectedState.expectedAudio,
        });
        if (recorder && recorder.state === "recording" && window.uploading) {
          clearInterval(window.local_checker);
          clearInterval(window.recording_interval);
          window.local_checker = undefined;
          window.recording_interval = undefined;
          Room.show_error_message(
            "ERROR: Your local recording has been disrupted. Iris could not restore the high-quality local camera and microphone stream. Your live call may continue, but local recording quality can no longer be guaranteed. <br><br>You will need to rejoin the recording to continue saving a high-quality local recording.",
          );
          Room.stop_showing_now_recording();
          Rails.ajax({
            url: `/recordings/${this.currentRecordingId || $("#recording_id").attr("data-recording-id")}/attendee/${$("#attendee_id").val()}/error.js?error=disrupted`,
            type: "post",
            error: function (xhr, status, ajaxError) {
              console.error("Failed to report disrupted error:", ajaxError);
              Rollbar.error("disrupted_error_report_failed", {
                status,
                error: ajaxError.toString(),
              });
            },
          });
        }
        return false;
      }

      Rollbar.warning("capture_invariant_repaired_via_update", {
        source,
        expectedVideo: expectedState.expectedVideo,
        expectedAudio: expectedState.expectedAudio,
      });
      return true;
    } finally {
      this.captureInvariantCheckInProgress = false;
    }
  }

  // Helper to queue database writes to prevent concurrent write corruption
  queueWrite(writeOperation) {
    const result = this.writeQueue.then(writeOperation);
    // Keep the queue alive on failure (prevent a rejected writeQueue from
    // blocking all future writes), but return the pre-catch promise so the
    // caller's own .catch() still fires and can retry on IDB errors.
    this.writeQueue = result.catch((error) => {
      console.error("Queued write operation failed:", error);
    });
    return result;
  }

  async save_blob(blob, index, current_recording_id, tries = 0) {
    var recording_id = current_recording_id;
    if (typeof recording_id == "undefined") {
      recording_id = parseInt(
        $("[data-recording-id]").attr("data-recording-id"),
      );
    }
    var attendee_id = $("#attendee_id").val();
    var blob_data = await new Response(blob).arrayBuffer();

    if (local_db.directUploadOnly || local_db.idbBlocked) {
      const camFile = new File([blob_data], `${index}_cam.webm`, {
        lastModified: new Date().getTime(),
        type: blob.type,
      });
      local_db.upload_file_direct(camFile, recording_id, attendee_id);
      return;
    }

    // Check if db is available and open
    if (typeof db === "undefined" || !db.isOpen()) {
      console.log("DB not open, attempting to reload");
      try {
        await local_db.load_local_db();
      } catch (error) {
        console.error("Failed to reload db:", error);
      }
    }

    if (typeof db === "undefined") {
      console.error(
        "save_blob: db unavailable after reload attempt, dropping blob",
      );
      Rollbar.error("save_blob_db_unavailable");
      return;
    }

    db.blobs
      .count()
      .then(function (count) {
        $("#current_count_of_files_left_to_upload").text(count + 1);
      })
      .catch(function (error) {
        console.log("Error counting blobs:", error);
      });

    var that = this;

    // Queue the write to prevent concurrent write corruption
    await this.queueWrite(async () => {
      return db.blobs.put({
        filename: index + "_cam.webm",
        blob_data: blob_data,
        recording_id: recording_id,
        attendee_id: attendee_id,
      });
    })
      .then(function (record) {
        console.log(`inserted ${index}_cam.webm into the local db`);
        db.recordings
          .where("recording_id")
          .equals(recording_id)
          .count()
          .then(function (count) {
            if (count === 0) {
              db.recordings.put({
                recording_id: recording_id,
                attendee_id: attendee_id,
              });
            }
          });
      })
      .catch(async function (error) {
        try {
          navigator.storage.estimate().then(function (data) {
            console.log(data["usage"] + " out of " + data["quota"] + " used.");
          });
        } catch (error) {}
        console.log("ERROR SAVING BLOB1: " + error + " " + tries);
        tries = tries + 1;
        if (tries < 10) {
          console.log("retry saving blob...");
          console.log(
            index +
              "_cam.webm" +
              " total size:" +
              blob_data.byteLength +
              " recording_id:" +
              recording_id +
              " attendee_id:" +
              attendee_id,
          );

          // Check if this is an IndexedDB corruption error
          const isCorruptionError =
            error.name === "UnknownError" ||
            error.message.includes("Internal error") ||
            error.message.includes("backing store") ||
            error.name === "DatabaseClosedError";

          // Check if this is a constraint error (duplicate key)
          const isConstraintError =
            error.name === "ConstraintError" ||
            error.message.includes("Key already exists");

          // If constraint error, blob already exists - consider it saved
          if (isConstraintError) {
            console.warn(
              `Blob ${index}_cam.webm already exists in database, skipping duplicate save`,
            );
            Rollbar.warning(
              `Duplicate blob save attempt for ${index}_cam.webm`,
              { recording_id, attendee_id },
            );
            return; // Exit without retrying
          }

          // Try to reload the database before retrying
          try {
            // Force recreation if corruption detected and first few tries
            const forceRecreate = isCorruptionError && tries <= 3;
            if (forceRecreate) {
              console.warn(
                `IndexedDB corruption detected on try ${tries}, forcing database recreation`,
              );
              Rollbar.warning(
                `IndexedDB corruption on blob save, attempt ${tries}: ${error.message}`,
              );
            }
            await local_db.load_local_db(forceRecreate);
            // Wait longer after reloading db to ensure it's ready
            setTimeout(
              function () {
                that.save_blob(blob, index, current_recording_id, tries);
              },
              forceRecreate ? 2000 : 1000,
            );
          } catch (dbError) {
            console.error("Failed to reload db:", dbError);
            if (dbError.name === "SecurityError") {
              Rollbar.error("save_blob_security_error", dbError.message);
              return;
            }
            if (
              dbError.name === "QuotaExceededError" ||
              dbError.message.includes("QuotaExceededError") ||
              dbError.message.includes("full disk")
            ) {
              Rollbar.warning("quota_exceeded_db_reload_direct_upload_cam", {
                recording_id,
                attendee_id,
                index,
              });
              const camFile = new File([blob_data], `${index}_cam.webm`, {
                lastModified: new Date().getTime(),
                type: blob.type,
              });
              local_db.upload_file_direct(camFile, recording_id, attendee_id);
              return;
            }
            setTimeout(function () {
              that.save_blob(blob, index, current_recording_id, tries);
            }, 1000);
          }
        } else {
          const isQuotaError = error.name === "QuotaExceededError" || error.message.includes("QuotaExceededError");
          const isIdbIOError = error.name === "AbortError" && (error.message.includes("DataError") || error.message.includes("IOError"));
          if (isQuotaError || isIdbIOError) {
            try {
              navigator.storage.estimate().then(function (data) {
                console.log(
                  data["usage"] + " out of " + data["quota"] + " used.",
                );
              });
            } catch (storageError) {}
            local_db.directUploadOnly = true;
            Rollbar.warning(isIdbIOError ? "idb_io_error_direct_upload_cam" : "quota_exceeded_direct_upload_cam", {
              recording_id,
              attendee_id,
              index,
            });
            Rails.ajax({
              url: `/recordings/${recording_id}/attendee/${attendee_id}/error.js?error=nospace`,
              type: "post",
              error: function (xhr, status, ajaxError) {
                console.error("Failed to report nospace error:", ajaxError);
                Rollbar.error("nospace_error_report_failed", {
                  status,
                  error: ajaxError.toString(),
                });
              },
            });
            const camFile = new File([blob_data], `${index}_cam.webm`, {
              lastModified: new Date().getTime(),
              type: blob.type,
            });
            local_db.upload_file_direct(camFile, recording_id, attendee_id);
          } else {
            console.error("ERROR SAVING BLOB1 - QUIT: " + error);
            Rollbar.error("ERROR SAVING BLOB (save_blob): " + error);
            alert(
              "You may need to use another browser. We were unable to save files locally. ERROR SAVING BLOB: " +
                error,
            );
          }
          // alert("Error with trying to save the blob. You either need to free up local space and restart the recording or update your browser. " + error)
        }
      });
  }

  async save_screen_blob(blob, index, current_recording_id, tries = 0) {
    var recording_id = current_recording_id;
    if (typeof recording_id == "undefined") {
      recording_id = parseInt(
        $("[data-recording-id]").attr("data-recording-id"),
      );
    }
    var attendee_id = options.share_uid.toString();
    var blob_data = await new Response(blob).arrayBuffer();

    if (local_db.directUploadOnly || local_db.idbBlocked) {
      const screenFile = new File([blob_data], `${index}_screen.webm`, {
        lastModified: new Date().getTime(),
        type: blob.type,
      });
      local_db.upload_file_direct(screenFile, recording_id, attendee_id);
      return;
    }

    // Check if db is available and open
    if (typeof db === "undefined" || !db.isOpen()) {
      console.log("DB not open for screen blob, attempting to reload");
      try {
        await local_db.load_local_db();
      } catch (error) {
        console.error("Failed to reload db:", error);
      }
    }

    if (typeof db === "undefined") {
      console.error(
        "save_screen_blob: db unavailable after reload attempt, dropping blob",
      );
      Rollbar.error("save_screen_blob_db_unavailable");
      return;
    }

    db.blobs
      .count()
      .then(function (count) {
        $("#current_count_of_files_left_to_upload").text(count + 1);
      })
      .catch(function (error) {
        console.log("Error counting blobs:", error);
      });

    var that = this;

    // Queue the write to prevent concurrent write corruption
    await this.queueWrite(async () => {
      return db.blobs.put({
        filename: index + "_screen.webm",
        blob_data: blob_data,
        recording_id: recording_id,
        attendee_id: attendee_id,
      });
    })
      .then(function (record) {
        console.log(
          `inserted screenshare ${index}_screen.webm into the local db`,
        );
      })
      .catch(async function (error) {
        console.error("ERROR SAVING SCREEN BLOB: " + error);
        tries = tries + 1;
        if (tries < 10) {
          console.log("retry saving screen blob...");

          // Check if this is an IndexedDB corruption error
          const isCorruptionError =
            error.name === "UnknownError" ||
            error.message.includes("Internal error") ||
            error.message.includes("backing store") ||
            error.name === "DatabaseClosedError";

          // Check if this is a constraint error (duplicate key)
          const isConstraintError =
            error.name === "ConstraintError" ||
            error.message.includes("Key already exists");

          // If constraint error, blob already exists - consider it saved
          if (isConstraintError) {
            console.warn(
              `Blob ${index}_screen.webm already exists in database, skipping duplicate save`,
            );
            Rollbar.warning(
              `Duplicate screen blob save attempt for ${index}_screen.webm`,
              { recording_id, attendee_id },
            );
            return; // Exit without retrying
          }

          // Try to reload the database before retrying
          try {
            // Force recreation if corruption detected and first few tries
            const forceRecreate = isCorruptionError && tries <= 3;
            if (forceRecreate) {
              console.warn(
                `IndexedDB corruption detected on screenshare save try ${tries}, forcing database recreation`,
              );
              Rollbar.warning(
                `IndexedDB corruption on screenshare save, attempt ${tries}: ${error.message}`,
              );
            }
            await local_db.load_local_db(forceRecreate);
            setTimeout(
              function () {
                that.save_screen_blob(blob, index, current_recording_id, tries);
              },
              forceRecreate ? 2000 : 1000,
            );
          } catch (dbError) {
            console.error("Failed to reload db:", dbError);
            if (dbError.name === "SecurityError") {
              Rollbar.error("save_screen_blob_security_error", dbError.message);
              return;
            }
            if (
              dbError.name === "QuotaExceededError" ||
              dbError.message.includes("QuotaExceededError") ||
              dbError.message.includes("full disk")
            ) {
              Rollbar.warning("quota_exceeded_db_reload_direct_upload_screen", {
                recording_id,
                attendee_id,
                index,
              });
              const screenFile = new File([blob_data], `${index}_screen.webm`, {
                lastModified: new Date().getTime(),
                type: blob.type,
              });
              local_db.upload_file_direct(
                screenFile,
                recording_id,
                attendee_id,
              );
              return;
            }
            setTimeout(function () {
              that.save_screen_blob(blob, index, current_recording_id, tries);
            }, 1000);
          }
        } else {
          const isQuotaError = error.name === "QuotaExceededError" || error.message.includes("QuotaExceededError");
          const isIdbIOError = error.name === "AbortError" && (error.message.includes("DataError") || error.message.includes("IOError"));
          if (isQuotaError || isIdbIOError) {
            local_db.directUploadOnly = true;
            Rollbar.warning(isIdbIOError ? "idb_io_error_direct_upload_screen" : "quota_exceeded_direct_upload_screen", {
              recording_id,
              attendee_id,
              index,
            });
            Rails.ajax({
              url: `/recordings/${recording_id}/attendee/${attendee_id}/error.js?error=nospace`,
              type: "post",
              error: function (xhr, status, ajaxError) {
                console.error("Failed to report nospace error:", ajaxError);
                Rollbar.error("nospace_error_report_failed", {
                  status,
                  error: ajaxError.toString(),
                });
              },
            });
            const screenFile = new File([blob_data], `${index}_screen.webm`, {
              lastModified: new Date().getTime(),
              type: blob.type,
            });
            local_db.upload_file_direct(screenFile, recording_id, attendee_id);
          } else {
            console.error("ERROR SAVING SCREEN BLOB - QUIT: " + error);
            Rollbar.error(
              "ERROR SAVING SCREEN BLOB (save_screen_blob): " + error,
            );
            alert("ERROR SAVING SCREEN BLOB: (ssb)" + error);
          }
        }
      });
  }

  async record(try_with_focus = true) {
    var that = this;
    let recorderStarted = false;

    // Prevent concurrent recording operations
    if (this.isRecordingStarting) {
      console.log(
        "Recording start already in progress, ignoring duplicate call",
      );
      return;
    }
    if (this.isRecordingStopping) {
      console.log("Recording is stopping, cannot start yet");
      return;
    }

    this.isRecordingStarting = true;

    try {
      if (typeof screenTracks !== "undefined") {
        if (screenTracks.videoTrack || screenTracks.audioTrack) {
          Room.record_screen_share();
        }
      }
      // return;

      // ensure no other recording is taking place
      if (recorder) {
        // Clean up old event handlers to prevent memory leaks
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        recorder.onstart = null;
        recorder.onpause = null;
        recorder.onresume = null;

        if (recorder.state == "recording") {
          await new Promise((resolve) => {
            recorder.addEventListener("stop", resolve, { once: true });
            try {
              recorder.stop();
            } catch (e) {
              console.error("Error stopping recorder:", e);
              resolve();
            }
          });
        }
        recorder = undefined;
      }

      var that = this;
      var bits_per_second = 2500000;
      if (current_camera_height >= 720) {
        bits_per_second = 5000000;
      }
      if (current_camera_height >= 1080) {
        bits_per_second = 8000000;
      }

      // its just too much data for the browser
      if (current_camera_height >= 2100) {
        bits_per_second = 16000000;
      }

      if (producer_role == false && user_joined_room == true) {
        // Clear any stale interval from a previous recording — new one starts after recorder.start()
        if (window.local_checker) {
          clearInterval(window.local_checker);
          window.local_checker = undefined;
        }

        if (local_media_stream && local_media_stream.getTracks().length > 2) {
          console.error(
            "more than 2 tracks?!? total tracks=" +
              local_media_stream.getTracks().length,
          );
        }

        if (!local_media_stream || local_media_stream.getTracks().length == 0) {
          console.warn(
            "no_tracks before recording — attempting update() to re-acquire stream. tracks=" +
              (local_media_stream
                ? local_media_stream.getTracks().length
                : "null"),
          );
          Rollbar.warning("no_tracks_retrying_update");
          await that.ensureCaptureInvariant({
            source: "record_start",
            forceUpdate: true,
          });
        }

        if (!local_media_stream || local_media_stream.getTracks().length == 0) {
          console.error(
            "0 tracks after update attempt, giving up. tracks=" +
              (local_media_stream
                ? local_media_stream.getTracks().length
                : "null"),
          );
          Rollbar.error("no_tracks");
          clearInterval(window.local_checker);
          clearInterval(window.recording_interval);
          window.local_checker = undefined;
          window.recording_interval = undefined;
          that.isRecordingStarting = false;
          return;
        }

        // Create a proxy stream for the recorder. This lets update() swap camera/mic
        // tracks in-place (add new before removing old) so the MediaRecorder never stops.
        // Stopping and restarting the recorder embeds a new WebM header mid-stream,
        // which corrupts the naive byte-concatenation done in AttendeeCreateFilesJob.
        window.recording_proxy_stream = new MediaStream();
        local_media_stream
          .getTracks()
          .forEach((t) => window.recording_proxy_stream.addTrack(t));

        try {
          if ($("#subscription_id").attr("data-max-width") == 0) {
            recorder = new MediaRecorder(window.recording_proxy_stream, {
              audioBitsPerSecond: 320000,
            });
          } else {
            recorder = new MediaRecorder(window.recording_proxy_stream, {
              mimeType: encoding_options,
              audioBitsPerSecond: 320000,
              videoBitsPerSecond: bits_per_second,
            });
          }
        } catch (err1) {
          console.log(err1);
          console.log(`cams length ${cams.length == 0}`);
          console.log(
            `audio only ${$("#subscription_id").attr("data-max-width") == 0}`,
          );
          Rollbar.error("failed_first_recording_start1");
          alert(
            "Window needs to be focused in order to start recording. We will now start recording once you click OK.",
          );
          try {
            await that.update();
            if (
              $("#subscription_id").attr("data-max-width") == 0 ||
              cams.length == 0
            ) {
              recorder = new MediaRecorder(
                window.recording_proxy_stream || local_media_stream,
                {
                  audioBitsPerSecond: 320000,
                },
              );
            } else {
              recorder = new MediaRecorder(
                window.recording_proxy_stream || local_media_stream,
                {
                  mimeType: encoding_options,
                  audioBitsPerSecond: 320000,
                  videoBitsPerSecond: bits_per_second,
                },
              );
            }
          } catch (err2) {
            console.log(err2);
            Rollbar.error("failed_final_recording_start1");
            // Clear intervals — recorder never started, checker would fire checker_no_recorder
            clearInterval(window.local_checker);
            clearInterval(window.recording_interval);
            window.local_checker = undefined;
            window.recording_interval = undefined;
            that.isRecordingStarting = false;
            throw err2;
          }
        }

        var current_recording_id = parseInt(
          $("[data-recording-id]").attr("data-recording-id"),
        );
        this.currentRecordingId = current_recording_id;

        recorder.ondataavailable = function (e) {
          try {
            if (!e.data || !e.data.size || e.data.size < 1) {
              if (recorder) {
                if (recorder.state == "recording") {
                  // Room.show_error_message("Your browser tried to save a recording without any data. Please try a different browser like Google Chrome or else contact support at support@iris.fm.")
                  if (!e.data || !e.data.size) {
                    console.error("NO DATA ondataavailable");
                    // setTimeout(function(){
                    Rails.ajax({
                      url: `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/error.js?error=zerofile`,
                      type: "post",
                      error: function (xhr, status, ajaxError) {
                        console.error(
                          "Failed to report zerofile error:",
                          ajaxError,
                        );
                        Rollbar.error("zerofile_error_report_failed", {
                          status,
                          error: ajaxError.toString(),
                        });
                      },
                    });
                    // }, 1000);
                  } else {
                    console.error(
                      "Too small DATA ondataavailable: " + e.data.size,
                    );
                    // setTimeout(function(){
                    Rails.ajax({
                      url:
                        `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/error.js?error=toosmallfile` +
                        e.data.size,
                      type: "post",
                      error: function (xhr, status, ajaxError) {
                        console.error(
                          "Failed to report toosmallfile error:",
                          ajaxError,
                        );
                        Rollbar.error("toosmallfile_error_report_failed", {
                          file_size: e.data.size,
                          status,
                          error: ajaxError.toString(),
                        });
                      },
                    });
                    // }, 1000);
                  }
                }
              }
            } else {
              window.last_ondataavailable = new Date();
              // Capture blob_index in closure to prevent race conditions
              const capturedIndex = blob_index++;
              that.save_blob(e.data, capturedIndex, current_recording_id);
              (function notifyFileSaved(attempt) {
                Rails.ajax({
                  url: `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/file_saved.js`,
                  type: "post",
                  error: function (xhr, status, ajaxError) {
                    if (attempt < 5) {
                      setTimeout(function() { notifyFileSaved(attempt + 1); }, 2000 * attempt);
                    } else {
                      console.error("Failed to notify server of file_saved:", ajaxError);
                      Rollbar.warning("file_saved_notification_failed", {
                        recording_id: current_recording_id,
                        status,
                        error: ajaxError.toString(),
                      });
                    }
                  },
                });
              })(1);
            }
          } catch (error) {
            console.error("Error in ondataavailable handler:", error);
            Rollbar.error("ondataavailable_handler_error", {
              error: error.toString(),
              message: error.message,
              stack: error.stack,
            });
          }
        };
        recorder.onstop = async function (event) {
          console.log("recorder stopped");
          console.log(new Date().toISOString());
          console.log(event);
          // Capture before resetting — true only when stopRecording() initiated the stop
          const wasIntentionalStop = that.isRecordingStopping;
          that.isRecordingStopping = false;
          recorder = undefined;
          // Clear the health-check interval so it doesn't fire against a null recorder
          clearInterval(window.local_checker);
          window.local_checker = undefined;
          // console.log(
          //   `onstop: wasIntentionalStop=${wasIntentionalStop} window.uploading=${window.uploading} blob_index=${blob_index}`,
          // );
          if (
            !wasIntentionalStop &&
            window.uploading &&
            !window.pageUnloading
          ) {
            console.log("recorder stopped unexpectedly, restarting");
            Rollbar.warning("recorder_unexpected_stop_restarting");
            try {
              await that.record();
            } catch (e) {
              Rollbar.error("recorder_restart_after_unexpected_stop_failed", {
                error: e.toString(),
              });
            }
          }
        };
        recorder.onerror = async function (event) {
          console.log("recorder error");
          console.log(encoding_options);
          console.error(`error recording stream: ${event.error.name}`);
          console.log(event.error.message);

          // Chrome fires InvalidModificationError when tracks are added/removed
          // from the proxy stream. It may have already transitioned the recorder
          // to inactive before firing this event, but the swap succeeded and the
          // recorder was capturing on the new tracks. Treat it as informational.
          if (event.error.name === "InvalidModificationError") {
            console.log(
              "InvalidModificationError during proxy track swap — ignoring",
            );
            Rollbar.info("proxy_swap_invalid_modification_ignored");
            return;
          }

          const isRecoverableEncodingFailure =
            event.error.name === "EncodingError" ||
            (event.error.name === "UnknownError" &&
              typeof event.error.message === "string" &&
              event.error.message
                .toLowerCase()
                .includes("video encoding failed"));

          // Some Android Chrome builds surface codec failures as UnknownError
          // instead of EncodingError. Treat both as recoverable codec failures.
          if (
            isRecoverableEncodingFailure &&
            window.encoding_fallbacks &&
            window.encoding_fallbacks.length > 0
          ) {
            const old_codec = encoding_options;
            encoding_options = window.encoding_fallbacks.shift();
            console.log(
              `Attempting to recover from codec failure. Switching from ${old_codec} to ${encoding_options}`,
            );
            Rollbar.warning(
              `Codec failure recovery: switching from ${old_codec} to ${encoding_options}`,
            );

            // Stop the current recorder
            if (recorder && recorder.state !== "inactive") {
              try {
                recorder.stop();
              } catch (e) {
                console.log("Error stopping recorder:", e);
              }
            }
            recorder = undefined;

            // Reset state flags for recovery
            that.isRecordingStopping = false;
            that.isRecordingStarting = false;

            // Wait a moment then restart recording with new codec
            setTimeout(async function () {
              try {
                await that.record(false);
                console.log(
                  "Successfully restarted recording with fallback codec: " +
                    encoding_options,
                );
              } catch (e) {
                console.error(
                  "Failed to restart recording with fallback codec:",
                  e,
                );
                Rollbar.error(
                  "Failed to restart recording with fallback codec: " +
                    e.message,
                );
                // Ensure flags are cleared on failure
                that.isRecordingStarting = false;
                that.isRecordingStopping = false;
                Rails.ajax({
                  url: `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/error.js?error=encoding_fallback_failed`,
                  type: "post",
                  error: function (xhr, status, ajaxError) {
                    console.error(
                      "Failed to report encoding_fallback_failed error:",
                      ajaxError,
                    );
                    Rollbar.error(
                      "encoding_fallback_failed_error_report_failed",
                      { status, error: ajaxError.toString() },
                    );
                  },
                });
                alert(
                  "Local Recording Error: Unable to find compatible encoding format. Please contact us at support@iris.fm",
                );
              }
            }, 500);
            return;
          }

          // If no fallback available or different error, show error message
          Rollbar.error(event.error.name + ": " + event.error.message);
          setTimeout(function () {
            Rails.ajax({
              url: `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/error.js?error=unknown`,
              type: "post",
              error: function (xhr, status, ajaxError) {
                console.error(
                  "Failed to report unknown recording error:",
                  ajaxError,
                );
                Rollbar.error("unknown_recording_error_report_failed", {
                  status,
                  error: ajaxError.toString(),
                });
              },
            });
          }, 1000);
          alert(
            "Local Recording Error: Please contact us at support@iris.fm " +
              event.error,
          );
          throw event.error;
        };
        recorder.onstart = function () {
          console.log("RECORDING started");
          console.log(new Date().toISOString());
        };
        recorder.onpause = function () {
          console.log("RECORDING paused");
        };
        recorder.onresume = function () {
          console.log("RECORDING resumed");
        };

        try {
          recorder.start(10000);
          window.last_ondataavailable = new Date();
          window.local_checker = setInterval(
            that.checkStillRunning.bind(that),
            10000,
          );
          that.scheduleEarlyRecorderFlush();
          recorderStarted = true;
          // Clear starting flag after successful start
          that.isRecordingStarting = false;
        } catch (err1) {
          console.log(err1);
          console.log(encoding_options);
          Rollbar.error("failed_first_recording_start2");
          alert(
            "Window needs to be focused in order to start recording. We will now start recording once you click OK.",
          );
          try {
            await that.update();
            recorder = new MediaRecorder(
              window.recording_proxy_stream || local_media_stream,
              {
                mimeType: encoding_options,
                audioBitsPerSecond: 320000,
                videoBitsPerSecond: bits_per_second,
              },
            );
            recorder.start(10000);
            window.last_ondataavailable = new Date();
            window.local_checker = setInterval(
              that.checkStillRunning.bind(that),
              10000,
            );
            that.scheduleEarlyRecorderFlush();
            recorderStarted = true;
            // Clear starting flag after successful start
            that.isRecordingStarting = false;
          } catch (err2) {
            console.log(err2);
            Rollbar.error("failed_final_recording_start2");
            // Clear starting flag on failure
            that.isRecordingStarting = false;
            throw err2;
          }
        }

        if (!recorderStarted) {
          return;
        }

        (function notifyRecordingStarted(attempt) {
          Rails.ajax({
            url: `/recordings/${current_recording_id}/attendee/${$("#attendee_id").val()}/recording_started.js`,
            type: "post",
            error: function (xhr, status, ajaxError) {
              if (attempt < 5) {
                setTimeout(function() { notifyRecordingStarted(attempt + 1); }, 2000 * attempt);
              } else {
                console.error("Failed to notify server of recording_started:", ajaxError);
                Rollbar.warning("recording_started_notification_failed", {
                  status,
                  error: ajaxError.toString(),
                });
              }
            },
          });
        })(1);
        if (create_transcripts && Transcribe.running != true) {
          Transcribe.start();
        }
      } else {
        // Not recording as guest/producer - clear starting flag
        this.isRecordingStarting = false;
      }
    } catch (error) {
      // Ensure starting flag is cleared on any error
      console.error("Error in record():", error);
      this.isRecordingStarting = false;
      throw error;
    }
  }

  checkStillRunning() {
    if (!recorder) {
      clearInterval(window.local_checker);
      clearInterval(window.recording_interval);
      window.local_checker = undefined;
      window.recording_interval = undefined;
      Rollbar.error("checker_no_recorder");
      Room.show_error_message(
        "Your recording stopped unexpectedly. Attempting to restart...",
      );
      this.record().catch((error) => {
        console.error(
          "Failed to restart recorder after checker_no_recorder:",
          error,
        );
        Rollbar.error("checker_no_recorder_restart_failed", {
          error: error.toString(),
          message: error.message,
          stack: error.stack,
        });
      });
      return;
    }

    if (recorder.state != "recording") {
      clearInterval(window.local_checker);
      clearInterval(window.recording_interval);
      window.local_checker = undefined;
      window.recording_interval = undefined;
      Rollbar.error("checker_not_recording_state");
      return;
    }

    if (!window.uploading) {
      clearInterval(window.local_checker);
      clearInterval(window.recording_interval);
      window.local_checker = undefined;
      window.recording_interval = undefined;
      Rollbar.error("checker_not_recording_state");
      return;
    }

    var video_state = "live";
    var audio_state = "live";
    var error = false;

    if (recorder) {
      if (recorder.stream.getVideoTracks().length > 0) {
        video_state = recorder.stream.getVideoTracks()[0].readyState;
      }

      if (recorder.stream.getAudioTracks().length > 0) {
        audio_state = recorder.stream.getAudioTracks()[0].readyState;
      }

      if (video_state != "live" || audio_state != "live") {
        error = true;
      }
    } else {
      // error = true;  //not sure if we need this.
    }

    var seconds_since_last_ondataavailable =
      (new Date().getTime() - window.last_ondataavailable.getTime()) / 1000;

    if (error) {
      clearInterval(window.local_checker);
      clearInterval(window.recording_interval);
      console.log("check room status to see if still recording");
      Rails.ajax({
        url: `/r/${$("[data-room-id]").attr("data-room-id")}/status.js?recording_id=${this.currentRecordingId || $("#recording_id").attr("data-recording-id")}`,
        type: "get",
        error: function (xhr, status, ajaxError) {
          console.error("Failed to check room status:", ajaxError);
          Rollbar.warning("check_room_status_failed", {
            status,
            error: ajaxError.toString(),
          });
        },
      });

      setTimeout(() => {
        if (recorder && recorder.state == "recording" && window.uploading) {
          //window.uploading means its recording locally
          console.log("show error since still recording....");
          console.error(
            "Monitoring error: disrupted video_state:" +
              video_state +
              " audio_state:" +
              audio_state,
          );
          Rollbar.error("Monitoring error: disrupted");
          Room.show_error_message(
            "ERROR: Your local recording has been disrupted. Most likely your camera or microphone has been disconnected. Your local video and audio are no longer being saved. <br><br>You will need to rejoin the recording to continue.",
          );
          Room.stop_showing_now_recording();
          // setTimeout(function(){
          Rails.ajax({
            url: `/recordings/${this.currentRecordingId || $("#recording_id").attr("data-recording-id")}/attendee/${$("#attendee_id").val()}/error.js?error=disrupted`,
            type: "post",
            error: function (xhr, status, ajaxError) {
              console.error("Failed to report disrupted error:", ajaxError);
              Rollbar.error("disrupted_error_report_failed", {
                status,
                error: ajaxError.toString(),
              });
            },
          });
          // }, 1000);
          $(
            "#stop_recording_button span, #guest_start_recording_button span",
          ).text("Recording Stopped");
        }
      }, 3000);
    } else if (seconds_since_last_ondataavailable > 60) {
      var any_working_tracks = false;
      if (localTracks.videoTrack) {
        if (localTracks.videoTrack._enabled) {
          any_working_tracks = true;
        }
      }
      if (localTracks.audioTrack) {
        if (localTracks.audioTrack._enabled) {
          any_working_tracks = true;
        }
      }

      if (any_working_tracks) {
        clearInterval(window.local_checker);
        clearInterval(window.recording_interval);
        // if (seconds_since_last_ondataavailable < 75) {
        console.error(
          "Monitoring error: notcalled time:" +
            seconds_since_last_ondataavailable,
        );
        Rollbar.error(
          "Monitoring error: notcalled time:",
          seconds_since_last_ondataavailable,
        );
        // }
        Room.show_error_message(
          "ERROR: Your local recording is NOT being saved because of an issue with your browser. <br><br>You can try to fix this by updating your browser or using Google Chrome or you can email our support at support@iris.fm for help.",
        );
        Room.stop_showing_now_recording();

        // setTimeout(function(){
        Rails.ajax({
          url: `/recordings/${this.currentRecordingId || $("#recording_id").attr("data-recording-id")}/attendee/${$("#attendee_id").val()}/error.js?error=notcalled`,
          type: "post",
          error: function (xhr, status, ajaxError) {
            console.error("Failed to report notcalled error:", ajaxError);
            Rollbar.error("notcalled_error_report_failed", {
              status,
              error: ajaxError.toString(),
            });
          },
        });
        // }, 1000);
        // $("#stop_recording_button span, #guest_start_recording_button span").text("Recording Stopped")
      }
    } else if (seconds_since_last_ondataavailable > 20) {
      var camera_on = $("#video-btn i").hasClass("fa-video");
      var mic_on = $("#mic-btn i").hasClass("fa-microphone");
      console.log(
        `manually request data camera_on:${camera_on} mic_on: ${mic_on} blob_index: ${blob_index}`,
      );
      // Add state check before calling requestData to prevent errors
      if (recorder && recorder.state === "recording") {
        try {
          recorder.requestData();
        } catch (e) {
          console.error("Error calling requestData:", e);
        }
      }
    }
  }

  // Called from beforeunload to flush the final buffered chunk into IndexedDB before
  // the page unloads. recorder.stop() synchronously fires ondataavailable with any
  // remaining data, which queues an IndexedDB write. The write may or may not finish
  // before the browser tears down the page, but it's worth attempting.
  scheduleEarlyRecorderFlush() {
    clearTimeout(this.earlyRecorderFlushTimer);
    this.earlyRecorderFlushTimer = setTimeout(() => {
      if (recorder && recorder.state === "recording") {
        try {
          recorder.requestData();
          console.log("early recorder.requestData() called");
        } catch (e) {
          console.error("early recorder.requestData() failed", e);
        }
      }
      this.earlyRecorderFlushTimer = null;
    }, 3000);
  }

  flushRecorderOnUnload() {
    if (recorder && recorder.state === "recording") {
      try {
        // Mark as intentional so onstop doesn't try to restart the recorder
        // (onstop checks isRecordingStopping to decide whether to auto-restart).
        this.isRecordingStopping = true;
        recorder.stop();
        console.log("flushRecorderOnUnload: recorder.stop() called");
      } catch (e) {
        this.isRecordingStopping = false;
        console.error("flushRecorderOnUnload: error stopping recorder", e);
      }
    }
    if (screenrecorder && screenrecorder.state === "recording") {
      try {
        screenrecorder.stop();
      } catch (e) {}
    }
  }

  stopRecording(recordingId = null) {
    // Capture recording_id on first call so retries use the same ID even if the DOM
    // has been updated to a new recording by the time the retry fires.
    const capturedRecordingId =
      recordingId || $("#recording_id").attr("data-recording-id");

    // Prevent concurrent stop operations
    if (this.isRecordingStopping) {
      console.log("Stop already in progress, ignoring duplicate call");
      return;
    }
    if (this.isRecordingStarting) {
      console.log("Recording is still starting, waiting before stop");
      // Wait for start to complete before stopping
      setTimeout(() => {
        this.stopRecording(capturedRecordingId);
      }, 500);
      return;
    }

    this.isRecordingStopping = true;

    try {
      clearTimeout(this.earlyRecorderFlushTimer);
      this.earlyRecorderFlushTimer = null;
      clearInterval(window.local_checker);
      clearInterval(window.recording_interval);
      window.local_checker = undefined;
      window.recording_interval = undefined;
      Transcribe.stop();

      if (screenrecorder) {
        if (screenrecorder.state != "inactive") {
          screenrecorder.stop();
        }
        Rails.ajax({
          url: `/recordings/${capturedRecordingId}/attendee/${options.share_uid}/recording_stopped.js`,
          type: "post",
          error: function (xhr, status, ajaxError) {
            console.error(
              "Failed to notify server of screen share recording_stopped:",
              ajaxError,
            );
            Rollbar.warning(
              "screen_share_recording_stopped_notification_failed_2",
              {
                share_uid: options.share_uid,
                status,
                error: ajaxError.toString(),
              },
            );
          },
        });
      }
      screenrecorder = undefined;
      window.recording_proxy_stream = undefined;

      if (recorder) {
        if (recorder.state == "recording") {
          recorder.stop();
          // Note: onstop handler will be called asynchronously and will clear flags
        } else {
          // Recorder not recording, clear flags immediately
          this.isRecordingStopping = false;
          recorder = undefined;
        }
      } else {
        // No recorder, clear stopping flag
        this.isRecordingStopping = false;
      }

      Rails.ajax({
        url: `/recordings/${capturedRecordingId}/attendee/${$("#attendee_id").val()}/recording_stopped.js`,
        type: "post",
        error: function (xhr, status, ajaxError) {
          console.error(
            "Failed to notify server of recording_stopped:",
            ajaxError,
          );
          Rollbar.warning("recording_stopped_notification_failed", {
            status,
            error: ajaxError.toString(),
          });
        },
      });
    } catch (error) {
      console.error("Error in stopRecording():", error);
      this.isRecordingStopping = false;
      throw error;
    }
  }

  async update_iOS() {
    if (this._iOS_update_in_progress) {
      console.log("update_iOS: skipping concurrent call");
      return;
    }
    this._iOS_update_in_progress = true;
    try {
      await this._update_iOS_inner();
    } finally {
      this._iOS_update_in_progress = false;
    }
  }

  async _update_iOS_inner() {
    if (local_audio_context) {
      if (local_audio_context.state == "running") {
        local_audio_context.close();
      }
    }

    if (local_media_stream) {
      local_media_stream.getTracks().forEach((track) => track.stop());
      local_media_stream = undefined;
    }

    if (localTracks.videoTrack && localTracks.audioTrack) {
      console.log("reset both tracks");
      // localTracks.videoTrack.close();
      // localTracks.videoTrack.close();

      if (client.uid) {
        await client.unpublish();
      }

      // Capture and clear track refs immediately to prevent race conditions
      // between concurrent _update_iOS_inner calls
      const audioTrack = localTracks.audioTrack;
      const videoTrack = localTracks.videoTrack;
      localTracks.audioTrack = undefined;
      localTracks.videoTrack = undefined;

      if (audioTrack) {
        await audioTrack.stop();
        await audioTrack.close();
      }

      if (videoTrack) {
        await videoTrack.stop();
        await videoTrack.close();
      }

      await navigator.mediaDevices
        .getUserMedia({
          audio: this.audio_options,
          video: {
            width: { ideal: $("#subscription_id").attr("data-max-width") },
            height: { ideal: $("#subscription_id").attr("data-max-height") },
            frameRate: local_frame_rate,
            deviceId: { exact: currentCam.deviceId },
          },
        })
        .then(async function (camera) {
          localTracks.audioTrack = AgoraRTC.createCustomAudioTrack({
            mediaStreamTrack: camera.getAudioTracks()[0],
          });

          // Clone camera stream for remote track with different constraints
          var remote_camera = camera.clone();
          await remote_camera
            .getVideoTracks()[0]
            .applyConstraints({ width: 640, height: 360 });
          await remote_camera
            .getVideoTracks()[0]
            .applyConstraints({ frameRate: 24 });
          localTracks.videoTrack = AgoraRTC.createCustomVideoTrack({
            mediaStreamTrack: remote_camera.getVideoTracks()[0],
            bitrateMax: 1600,
          });

          // Store reference to cloned stream for later cleanup
          if (window.cloned_remote_camera) {
            window.cloned_remote_camera
              .getTracks()
              .forEach((track) => track.stop());
          }
          window.cloned_remote_camera = remote_camera;

          if ($("#pre-local-player").length > 0) {
            localTracks.videoTrack.play("pre-local-player");
          }
          if (client.uid) {
            await client.publish(Object.values(localTracks));
            await localTracks.videoTrack.play(`player-${options.uid}`);
          }

          if (
            $("#subscription_id").attr("data-max-width") != 0 &&
            cams.length > 0
          ) {
            local_media_stream = camera;
          } else {
            local_media_stream = new MediaStream([camera.getAudioTracks()[0]]);
          }
        })
        .catch(async function (error) {
          // should try one more time without constraints - OverconstrainedError: Invalid constraint
          console.error("Cant get devices round for mobile", error);
          Rollbar.error("cant get mobile devices", error);
          alert(error);
        });

      if (local_media_stream) {
        // NEED TO CHANGE THIS PROCESS
        if (local_media_stream.getVideoTracks()[0]) {
          this.show_video_dimensions();
        }

        VolumeMeter.bind(local_media_stream);
        if (local_media_stream.getVideoTracks()[0]) {
          Rails.ajax({
            url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?camera=${local_media_stream.getVideoTracks()[0].label}`,
            type: "patch",
            error: function (xhr, status, ajaxError) {
              console.error("Failed to update camera meta (iOS):", ajaxError);
              Rollbar.warning("update_camera_meta_failed_ios", {
                status,
                error: ajaxError.toString(),
              });
            },
          });
        }
        Rails.ajax({
          url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?microphone=${local_media_stream.getAudioTracks()[0].label}`,
          type: "patch",
          error: function (xhr, status, ajaxError) {
            console.error("Failed to update microphone meta (iOS):", ajaxError);
            Rollbar.warning("update_microphone_meta_failed_ios", {
              status,
              error: ajaxError.toString(),
            });
          },
        });
      }
    } else if (localTracks.audioTrack) {
      console.log("reset one track");

      if (client.uid) {
        if (localTracks.audioTrack) {
          await client.unpublish(localTracks.audioTrack);
        }
      }
      if (localTracks.audioTrack) {
        localTracks.audioTrack.close();
      }

      await navigator.mediaDevices
        .getUserMedia({ audio: this.audio_options, video: false })
        .then(async function (tracks) {
          localTracks.audioTrack = AgoraRTC.createCustomAudioTrack({
            mediaStreamTrack: tracks.getAudioTracks()[0],
          });
          if (client.uid) {
            await client.publish(Object.values(localTracks));
          }
          local_media_stream = tracks;
        })
        .catch(async function (error) {
          // should try one more time without constraints - OverconstrainedError: Invalid constraint
          console.error("Cant get devices round for mobile 2", error);
          Rollbar.error("cant get mobile devices 2", error);
          alert(error);
        });

      if (local_media_stream) {
        VolumeMeter.bind(local_media_stream);
        Rails.ajax({
          url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?microphone=${local_media_stream.getAudioTracks()[0].label}`,
          type: "patch",
          error: function (xhr, status, ajaxError) {
            console.error(
              "Failed to update microphone meta (audio only):",
              ajaxError,
            );
            Rollbar.warning("update_microphone_meta_failed_audio_only", {
              status,
              error: ajaxError.toString(),
            });
          },
        });
      }
    } else if (localTracks.videoTrack) {
      local_media_stream = new MediaStream([
        localTracks.videoTrack._originMediaStreamTrack,
      ]);
      // VolumeMeter.bind(local_media_stream);
      Rails.ajax({
        url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?camera=${local_media_stream.getVideoTracks()[0].label}`,
        type: "patch",
        error: function (xhr, status, ajaxError) {
          console.error(
            "Failed to update camera meta (video only):",
            ajaxError,
          );
          Rollbar.warning("update_camera_meta_failed_video_only", {
            status,
            error: ajaxError.toString(),
          });
        },
      });
    }
  }

  async update(retry = true) {
    var that = this;

    // Check if MediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error("MediaDevices API not available");
      // alert(
      //   "Your browser does not support media capture.\n\nPlease try:\n1. Use a modern browser (Chrome, Edge, Firefox)\n2. Ensure you are on a secure HTTPS connection\n3. Update your browser to the latest version\n4. Check your browser security settings",
      // );
      Rollbar.error("MEDIA_DEVICES_NOT_AVAILABLE");
      $("#no_permissions").show();
      return;
    }

    if (Room.is_iOS()) {
      //Room.is_iOS()
      await this.update_iOS();
    } else {
      const previousStream = local_media_stream;
      let replacementStream = null;

      // When actively recording, try to swap camera/mic tracks on the proxy stream
      // without stopping the recorder. Stopping and restarting MediaRecorder emits a
      // fresh WebM header as its first chunk, which corrupts the byte-concatenated
      // assembly in AttendeeCreateFilesJob. We add new tracks before removing old
      // ones so the proxy stream never momentarily goes empty.
      let proxySwapSucceeded = false;
      if (
        window.recording_proxy_stream &&
        recorder &&
        recorder.state === "recording"
      ) {
        try {
          const newStream = await navigator.mediaDevices
            .getUserMedia({
              audio: this.audio_options,
              video: this.exact_video_options || this.video_options,
            })
            .catch(() =>
              navigator.mediaDevices.getUserMedia({
                audio: this.audio_options,
                video: this.video_options,
              }),
            );
          const oldProxyTracks = window.recording_proxy_stream.getTracks();

          newStream
            .getTracks()
            .forEach((t) => window.recording_proxy_stream.addTrack(t));

          const removeOldProxyTracks = () => {
            oldProxyTracks.forEach((t) => {
              window.recording_proxy_stream.removeTrack(t);
              t.stop();
            });
          };

          // Remove old tracks immediately if new tracks are already live; otherwise
          // give the browser 300ms to spin up the new encoder before removing them.
          const newProxyTracks = newStream.getTracks();
          const immediatelyLive =
            newProxyTracks.length > 0 &&
            newProxyTracks.every((t) => t.readyState === "live");
          if (immediatelyLive) {
            removeOldProxyTracks();
          } else {
            console.log(
              "new proxy tracks not yet live, delaying old track removal by 300ms",
            );
            setTimeout(removeOldProxyTracks, 300);
          }
          if (local_media_stream)
            local_media_stream.getTracks().forEach((t) => t.stop());
          if (local_audio_context && local_audio_context.state === "running") {
            local_audio_context.close().catch(() => {});
          }
          local_media_stream = newStream;
          proxySwapSucceeded = true;
          console.log(
            "proxy_track_swap_success: recorder continues uninterrupted",
          );
          Rollbar.info("proxy_track_swap_success");
        } catch (swapErr) {
          console.warn(
            "proxy_track_swap_failed, falling back to normal update:",
            swapErr.message,
          );
          Rollbar.warning("proxy_track_swap_failed", {
            error: swapErr.toString(),
          });
        }
      }

      if (!proxySwapSucceeded) {
        replacementStream = await this.acquireReplacementMediaStream(retry);

        if (replacementStream) {
          await this.closeSpecificStream(previousStream);
          local_media_stream = replacementStream;
        } else if (
          previousStream &&
          previousStream
            .getTracks()
            .some((track) => track.readyState === "live")
        ) {
          local_media_stream = previousStream;
          Rollbar.warning("update_reused_previous_local_stream");
        } else {
          local_media_stream = undefined;
        }
      } // end if (!proxySwapSucceeded)

      // set some events and show data
      if (local_media_stream) {
        if (local_media_stream.getVideoTracks()[0]) {
          // Remove old event listeners first to prevent memory leaks from multiple assignments
          local_media_stream.getVideoTracks()[0].onended = null;
          local_media_stream.onended = null;
          local_media_stream.onremovetrack = null;

          // Add new event listeners
          local_media_stream.getVideoTracks()[0].onended = function () {
            console.log("VIDEO ENDED");
            that.update();
          };
          local_media_stream.onended = function () {
            console.log("--- ENDED");
          };
          local_media_stream.onremovetrack = (event) => {
            console.log(`${event.track.kind} track removed`);
          };
          that.show_video_dimensions();
          Rails.ajax({
            url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?camera=${local_media_stream.getVideoTracks()[0].label}`,
            type: "patch",
            error: function (xhr, status, ajaxError) {
              console.error("Failed to update camera meta:", ajaxError);
              Rollbar.warning("update_camera_meta_failed", {
                status,
                error: ajaxError.toString(),
              });
            },
          });
        } else {
          $("#current_video_resolution, #current_video_fps").hide();
        }
        if (local_media_stream.getAudioTracks()[0]) {
          VolumeMeter.bind(local_media_stream);
          // Remove old event listener first to prevent memory leaks
          local_media_stream.getAudioTracks()[0].onended = null;
          local_media_stream.getAudioTracks()[0].onended = function () {
            console.log("Audio ENDED");
            that.update();
          };
          Rails.ajax({
            url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?microphone=${local_media_stream.getAudioTracks()[0].label}`,
            type: "patch",
            error: function (xhr, status, ajaxError) {
              console.error("Failed to update microphone meta:", ajaxError);
              Rollbar.warning("update_microphone_meta_failed", {
                status,
                error: ajaxError.toString(),
              });
            },
          });
          // console.log(local_media_stream.getAudioTracks()[0].getSettings())
        }
      }
    }
  }

  async acquireReplacementMediaStream(retry = true) {
    let lastError = null;
    const tryGetUserMedia = async (constraints, label) => {
      try {
        lastError = null;
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error) {
        lastError = error;
        console.log(`${label}`, error.message);
        return null;
      }
    };

    let stream = await tryGetUserMedia(
      {
        audio: this.audio_options,
        video: this.exact_video_options,
      },
      "Cant get devices round 1",
    );
    if (stream) return stream;

    stream = await tryGetUserMedia(
      {
        audio: this.audio_options,
        video: this.video_options,
      },
      "Cant get devices round 2",
    );
    if (stream) return stream;

    const permissionDeniedError =
      lastError &&
      lastError.toString &&
      (lastError.toString().includes("PERMISSION_DENIED") ||
        lastError.toString().includes("Permission denied"));

    if (permissionDeniedError) {
      $("#no_permissions").show();
      console.log(
        "local_capture_failure_0 Unable to capture your camera. Please check logs." +
          lastError,
      );
      return null;
    }

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: this.audio_options,
        video: {
          width: 1280,
          height: 720,
          frameRate: local_frame_rate,
        },
      });
      return stream;
    } catch (error) {
      console.log("Cant get devices round 3", error.message);

      if (error.toString().includes("Could not start video source")) {
        alert(
          "Unable to capture your camera. Check that your webcam is not in use by another application. You may need to close other applications or tabs or restart your browser to free up the camera so it can be used by Iris. Also you can click the camera icon in the address bar of your browser to ensure you have granted permissions.\n\n Error:" +
            error,
        );
        console.log(
          "local_capture_failure_1A Unable to capture your camera. Please check logs. " +
            error,
        );
        if (retry) {
          Devices.loadCameras();
          var new_camera = cams.filter(
            (cam) => cam.label != currentCam.label,
          )[0];
          if (new_camera) {
            console.log("retry but change video source");
            $("#video_setting").val(new_camera.label);
            $("#video_setting").change();
          }
        }
        return await tryGetUserMedia(
          { audio: this.audio_options, video: false },
          "Cant get devices round 3A",
        );
      }
    }

    stream = await tryGetUserMedia(
      {
        audio: this.audio_options,
        video: true,
      },
      "Cant get devices round 4:",
    );
    if (stream) return stream;

    stream = await tryGetUserMedia(
      { audio: true, video: true },
      "Cant get devices round 5:",
    );
    if (stream) return stream;

    stream = await tryGetUserMedia(
      { audio: true },
      "Cant get devices round 6:",
    );
    if (stream) return stream;

    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true });
      return stream;
    } catch (error) {
      console.error("Cant get devices final round 7:", error.message);

      if (error.name === "NotSupportedError") {
        alert(
          "Your browser or system does not support media capture for video calls.\n\nThis could be caused by:\n• Outdated browser version - Please update your browser\n• Missing system drivers - Update your audio/video drivers\n• Browser security settings - Check your browser settings\n• Corporate security policies blocking media access\n\nPlease try:\n1. Update your browser to the latest version\n2. Update your operating system\n3. Try a different browser (Chrome, Edge, Firefox)\n4. Contact your IT administrator if on a work computer\n\nError: " +
            error.message,
        );
        Rollbar.error("CANT_GET_LOCAL_DEVICES_NOT_SUPPORTED", error);
      } else if (cams.length != 0 && mics.length != 0) {
        alert(
          "Unable to capture your devices. Check that your devices are not in use by another application. You may need to close other applications or tabs or restart your browser to free up the device so it can be used by Iris. Also you can click the camera/microphone icon in the address bar of your browser to ensure you have granted permissions.\n\n Error:" +
            error,
        );
        Rollbar.error("CANT_GET_LOCAL_DEVICES_FINAL", error);
      } else {
        $("#no_permissions").show();
        console.log("agora couldnt get anything");
      }
    }

    return null;
  }

  async closeSpecificStream(stream) {
    if (!stream) return;

    if (stream.getVideoTracks()[0]) {
      stream.getVideoTracks()[0].onended = null;
    }
    if (stream.getAudioTracks()[0]) {
      stream.getAudioTracks()[0].onended = null;
    }
    stream.onended = null;
    stream.onremovetrack = null;
    stream.getTracks().forEach((track) => track.stop());
  }

  get video_options() {
    // dont use camera if width is 0
    if ($("#subscription_id").attr("data-max-width") == 0 || cams.length == 0) {
      return false;
    } else {
      if (currentCam != undefined) {
        return {
          width: { ideal: $("#subscription_id").attr("data-max-width") },
          height: { ideal: $("#subscription_id").attr("data-max-height") },
          frameRate: local_frame_rate,
          deviceId: { exact: currentCam.deviceId },
        };
      } else {
        return {
          width: { ideal: $("#subscription_id").attr("data-max-width") },
          height: { ideal: $("#subscription_id").attr("data-max-height") },
          frameRate: local_frame_rate,
        };
      }
    }
  }

  get exact_video_options() {
    if ($("#subscription_id").attr("data-max-width") == 0 || cams.length == 0) {
      return false;
    } else {
      if (currentCam != undefined) {
        return {
          width: { ideal: $("#subscription_id").attr("data-max-width") },
          height: { ideal: $("#subscription_id").attr("data-max-height") },
          frameRate: { exact: local_frame_rate },
          deviceId: { exact: currentCam.deviceId },
        };
      } else {
        return {
          width: { ideal: $("#subscription_id").attr("data-max-width") },
          height: { ideal: $("#subscription_id").attr("data-max-height") },
          frameRate: { exact: local_frame_rate },
        };
      }
    }
  }

  get audio_options() {
    if (currentMic != undefined) {
      return {
        deviceId: { exact: currentMic.deviceId },
        channelCount: 1,
        noiseSuppression: options.noise_reduction,
        autoGainControl: false,
        sampleRate: audio_sample_rate,
        echoCancellation: options.echo_cancellation,
      };
    } else {
      return {
        channelCount: 1,
        noiseSuppression: options.noise_reduction,
        sampleRate: audio_sample_rate,
        echoCancellation: options.echo_cancellation,
        autoGainControl: false,
      };
    }
  }

  // Swaps only the audio track in local_media_stream (and recording proxy stream) using an
  // audio-only getUserMedia call. This avoids the macOS Chrome issue where requesting audio+video
  // together locks Chrome into Apple's VPIO audio unit, which ignores noiseSuppression: false.
  // _audio_track_updating is set by the caller (recreateAudioTrack) to cover the full operation.
  async updateAudioTrack({ releaseExistingTracks = false } = {}) {
    console.log(
      "updateAudioTrack start: echo_cancellation =",
      options.echo_cancellation,
      "noise_reduction =",
      options.noise_reduction,
    );
    // Use { exact: value } instead of plain booleans. Chrome treats plain booleans as
    // preferences it can silently ignore — on macOS, VPIO overrides noiseSuppression:false
    // when it's a plain value. With exact constraints Chrome must find a pipeline that
    // truly satisfies all of them, forcing it off the VPIO device onto one that actually
    // honours noiseSuppression:false alongside echoCancellation:true.
    const audioOpts = {
      echoCancellation: { exact: options.echo_cancellation },
      noiseSuppression: { exact: options.noise_reduction },
      autoGainControl: { exact: false },
    };
    // Prefer the current physical device but don't require it — exact deviceId would
    // prevent Chrome from picking a different virtual pipeline to satisfy the constraints.
    const currentDeviceId = local_media_stream
      ?.getAudioTracks()[0]
      ?.getSettings().deviceId;
    if (currentDeviceId) {
      audioOpts.deviceId = { ideal: currentDeviceId };
    }

    // Close the VolumeMeter AudioContext first. Its MediaStreamAudioSourceNode holds a
    // reference to the underlying audio device through the node graph. On macOS, this
    // keeps the device "in use" by VPIO even after the tracks are stopped, causing the
    // subsequent getUserMedia to be assigned to the same VPIO pipeline.
    if (local_audio_context && local_audio_context.state === "running") {
      await local_audio_context.close().catch(() => {});
    }

    // Keep the existing track alive during the first acquisition attempt so we do
    // not strand the user without audio if Safari rejects the requested constraints.
    // We only perform a destructive release-and-reacquire pass if explicitly asked.
    const oldTracks = new Set();
    if (releaseExistingTracks) {
      if (local_media_stream) {
        local_media_stream.getAudioTracks().forEach((t) => {
          t.onended = null;
          local_media_stream.removeTrack(t);
          oldTracks.add(t);
        });
      }
      if (window.recording_proxy_stream) {
        window.recording_proxy_stream.getAudioTracks().forEach((t) => {
          window.recording_proxy_stream.removeTrack(t);
          oldTracks.add(t);
        });
      }
      oldTracks.forEach((t) => {
        try {
          t.stop();
        } catch (e) {}
      });
    }

    const audioConstraintAttempts = [audioOpts];

    // iOS Safari often rejects strict { exact: ... } audio constraints with
    // OverconstrainedError("Invalid constraint"), especially for
    // noiseSuppression / autoGainControl. Retry with progressively looser
    // constraints before giving up.
    audioConstraintAttempts.push({
      echoCancellation: options.echo_cancellation,
      noiseSuppression: options.noise_reduction,
      autoGainControl: false,
      ...(currentDeviceId ? { deviceId: { ideal: currentDeviceId } } : {}),
    });
    audioConstraintAttempts.push({
      echoCancellation: options.echo_cancellation,
      ...(currentDeviceId ? { deviceId: { ideal: currentDeviceId } } : {}),
    });
    audioConstraintAttempts.push(true);

    let newStream;
    let lastError;

    let appliedConstraintAttemptIndex = -1;
    for (let i = 0; i < audioConstraintAttempts.length; i++) {
      const attempt = audioConstraintAttempts[i];
      try {
        console.log(
          "updateAudioTrack requesting getUserMedia with:",
          typeof attempt === "boolean"
            ? String(attempt)
            : JSON.stringify(attempt),
        );
        newStream = await navigator.mediaDevices.getUserMedia({
          audio: attempt,
          video: false,
        });
        appliedConstraintAttemptIndex = i;
        break;
      } catch (err) {
        lastError = err;
        console.warn("updateAudioTrack getUserMedia failed:", err);
      }
    }

    if (!newStream) {
      Rollbar.warning("update_audio_track_all_constraint_attempts_failed", {
        error: lastError ? lastError.toString() : "unknown",
        releaseExistingTracks,
      });
      return;
    }

    const newAudioTrack = newStream.getAudioTracks()[0];
    if (!newAudioTrack) return null;
    const newTrackSettings = newAudioTrack.getSettings();
    console.log("updateAudioTrack new track settings:", newTrackSettings);

    // Verify the browser actually honored the requested exact constraints.
    // Chrome on macOS silently accepts { exact: value } but can return a track
    // that doesn't honor them — most commonly noiseSuppression:false is ignored
    // when the previous audio session is still held open (keeping VPIO active).
    if (appliedConstraintAttemptIndex === 0) {
      const settingsMismatch =
        (newTrackSettings.echoCancellation !== undefined &&
          newTrackSettings.echoCancellation !== options.echo_cancellation) ||
        (newTrackSettings.noiseSuppression !== undefined &&
          newTrackSettings.noiseSuppression !== options.noise_reduction) ||
        (newTrackSettings.autoGainControl !== undefined &&
          newTrackSettings.autoGainControl !== false);

      if (settingsMismatch) {
        if (!releaseExistingTracks) {
          // Non-destructive attempt got wrong settings. Return null so the caller
          // retries with releaseExistingTracks:true, which fully tears down the old
          // audio pipeline before reacquiring — giving the browser a clean session.
          console.warn(
            "updateAudioTrack: exact constraints not honored, retrying with full teardown",
            {
              requested: {
                echoCancellation: options.echo_cancellation,
                noiseSuppression: options.noise_reduction,
                autoGainControl: false,
              },
              actual: newTrackSettings,
            },
          );
          try {
            newAudioTrack.stop();
          } catch (e) {}
          return null;
        }
        // Even after full teardown the browser can't honor the constraints (hardware
        // limitation). Mark as non-exact so the caller shows a warning and rolls back.
        console.warn(
          "updateAudioTrack: exact constraints not honored even after full teardown — hardware limitation",
          {
            requested: {
              echoCancellation: options.echo_cancellation,
              noiseSuppression: options.noise_reduction,
              autoGainControl: false,
            },
            actual: newTrackSettings,
          },
        );
        appliedConstraintAttemptIndex = 1;
      }
    }

    if (window.recording_proxy_stream) {
      if (!releaseExistingTracks) {
        window.recording_proxy_stream.getAudioTracks().forEach((t) => {
          window.recording_proxy_stream.removeTrack(t);
          oldTracks.add(t);
        });
      }
      window.recording_proxy_stream.addTrack(newAudioTrack);
    }

    if (local_media_stream) {
      if (!releaseExistingTracks) {
        local_media_stream.getAudioTracks().forEach((t) => {
          t.onended = null;
          local_media_stream.removeTrack(t);
          oldTracks.add(t);
        });
      }
      local_media_stream.addTrack(newAudioTrack);
      VolumeMeter.bind(local_media_stream);
      newAudioTrack.onended = null;
      newAudioTrack.onended = () => {
        console.log("Audio ENDED");
        this.update();
      };
    }

    oldTracks.forEach((t) => {
      try {
        t.stop();
      } catch (e) {}
    });

    return {
      track: newAudioTrack,
      appliedExactly: appliedConstraintAttemptIndex === 0,
      appliedConstraintAttemptIndex,
      releaseExistingTracks,
    };
  }

  async closeExistingTracks() {
    if (local_media_stream) {
      // Remove event listeners before stopping tracks to prevent memory leaks
      if (local_media_stream.getVideoTracks()[0]) {
        local_media_stream.getVideoTracks()[0].onended = null;
      }
      if (local_media_stream.getAudioTracks()[0]) {
        local_media_stream.getAudioTracks()[0].onended = null;
      }
      local_media_stream.onended = null;
      local_media_stream.onremovetrack = null;

      local_media_stream.getTracks().forEach((track) => track.stop());
    }
    local_media_stream = undefined;

    // Clean up cloned remote camera stream
    if (window.cloned_remote_camera) {
      window.cloned_remote_camera.getTracks().forEach((track) => track.stop());
      window.cloned_remote_camera = undefined;
    }

    if (local_audio_context) {
      if (local_audio_context.state == "running") {
        try {
          await local_audio_context.close();
        } catch (InvalidStateError) {
          console.log("InvalidStateError: Can't close an AudioContext twice ");
        }
      }
    }
  }

  show_video_dimensions() {
    if (local_media_stream) {
      var video_track_settings = local_media_stream
        .getVideoTracks()[0]
        .getSettings();
      var width = video_track_settings.width;
      var height = video_track_settings.height;
      var fps = video_track_settings.frameRate;
      current_camera_height = height;
      $("#current_video_resolution")
        .show()
        .text(width + " x " + height);
      $("#current_video_fps")
        .show()
        .text(fps + " fps");
      Rails.ajax({
        url: `/${subscription_id}/attendee/${$("#attendee_id").val()}/update_meta.js?height=${height}&fps=${fps}`,
        type: "patch",
        error: function (xhr, status, ajaxError) {
          console.error("Failed to update video dimensions meta:", ajaxError);
          Rollbar.warning("update_video_dimensions_meta_failed", {
            height,
            fps,
            status,
            error: ajaxError.toString(),
          });
        },
      });
    }
  }
}
